import { AnyAction, createAsyncThunk, Dispatch, ThunkAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { HttpStatus } from 'constants/statusCodes';

import { getCookie, setCookie } from 'helpers/cookie';
import { handleRequestError, isNetworkError } from 'helpers/network';
import { getTranslationKey } from 'helpers/texting';
import { getEndCustomer } from 'old-store/actions';
import { RootState } from 'old-store/store';
import { ISelection } from 'old-store/types/gallery';
import { Api } from 'old-store/utils';
import ApiErrors from 'old-store/utils/API/APIErrors';
import { toast } from 'react-toastify';
import {
  addSelectionEditImages,
  addLikes,
  addSelection,
  finishSelection,
  handleLikes,
  removeSelectionEditImages,
  removeLikes,
  startSelection,
  toggleSelectionEditImage,
  ISelectionState
} from 'store/slices/selection';
import { ICollection } from 'types/collection';

export interface ISelectionSaveValues {
  selectionName: string | null;
  message?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  withCongratsModal?: boolean;
  withPasswordModal?: boolean;
  successMessageKey?: string;
}

interface ISelectionSaveRequest {
  values: ISelectionSaveValues;
  state: RootState;
  dispatch: Dispatch<any>;
  predefinedSelectionId?: string;
  updateSelectionState: boolean;
}

export function checkIfAuthorizedAndLogoutIfNot(code: number, user: string = '') {
  if (code === HttpStatus.Forbidden || code === HttpStatus.NotFound) {
    if (user !== '') {
      const currentLocalStorageValue = localStorage.getItem('endCustomerToken');
      const currentLocalStorageTokenMap = currentLocalStorageValue
        ? JSON.parse(currentLocalStorageValue)
        : {};

      localStorage.setItem(
        'endCustomerToken',
        JSON.stringify({
          ...currentLocalStorageTokenMap,
          [user]: undefined
        })
      );

      setCookie('endCustomerToken', '');
    }
    window.location.reload();
  }
}

export const fetchCustomerToken = async (user: string) => {
  try {
    const actualEndCustomerCookie = getCookie('endCustomerToken');

    if (actualEndCustomerCookie) {
      const { SITE_ID: collectionId } = window;

      const endCustomerTokenIsValid = await axios
        .get('/api/end-customers/validate-token', {
          headers: {
            authorizationendcustomer: actualEndCustomerCookie
          },
          params: {
            collectionId
          },
          validateStatus: (status) => status === 200 || status === 403
        })
        .then((response) => response.status === 200);

      if (endCustomerTokenIsValid) {
        const currentLocalStorageValue = localStorage.getItem('endCustomerToken');
        const currentLocalStorageTokenMap = currentLocalStorageValue
          ? JSON.parse(currentLocalStorageValue)
          : {};

        localStorage.setItem(
          'endCustomerToken',
          JSON.stringify({
            ...currentLocalStorageTokenMap,
            [user]: actualEndCustomerCookie
          })
        );
      }
    }

    const persistData = localStorage.getItem('endCustomerToken');
    const endCustomerTokenMap = persistData ? JSON.parse(persistData) : {};
    const currentToken = endCustomerTokenMap[user];

    if (currentToken) {
      setCookie('endCustomerToken', currentToken, 30);

      return currentToken;
    }

    const {
      result: { token, flyUserId }
    } = await Api.TempCustomer.createTempCustomer();

    localStorage.setItem('flyUserId', flyUserId);
    localStorage.setItem(
      'endCustomerToken',
      JSON.stringify({ ...endCustomerTokenMap, [user]: token })
    );

    setCookie('endCustomerToken', token, 30);

    return token;
  } catch (e) {
    const requestError = handleRequestError(e);

    console.log(requestError);
    throw requestError;
  }
};

export const getCustomerSelectionId = async (
  selection: ISelectionState,
  dispatch: Dispatch<any>,
  user: string = ''
) => {
  try {
    const currentSelection = selection.all.find((item) => item.current);

    if (currentSelection) return currentSelection._id;

    const newSelection = await Api.TempCustomer.createSelection();
    ApiErrors.checkOnApiError(newSelection);

    // covers edge case when like fails due to corrupted cookie
    // if (!newSelection._id) {
    //   localStorage.removeItem('endCustomerToken');

    //   return;
    // }

    dispatch(addSelection(newSelection));

    await Api.TempCustomer.setSelection({
      collection_id: window.SITE_ID,
      selection_id: newSelection._id
    });

    return newSelection._id;
  } catch (e: any) {
    checkIfAuthorizedAndLogoutIfNot(e.code, user);

    console.log('e', e);
    throw e;
  }
};

let isLikeRequestProcessing = false;
const likeRequestQueue: string[] = [];

interface ILikeImageRequest {
  imageId: string;
  optimisticUpdate?: boolean;
}

class LikeRequestError extends Error {
  public code: number;

  constructor(message: string, code: number) {
    super(message);
    this.name = 'Like Request Error';
    this.code = code;
  }
}

const getUnavailableLikeRequestError = () =>
  new LikeRequestError(
    navigator.onLine ? 'Like request failed' : 'Network Error',
    navigator.onLine ? HttpStatus.InternalServerError : 0
  );

const checkLikeRequestResponse = (response: any) => {
  if (isNetworkError(response)) {
    throw new LikeRequestError(response?.message || 'Network Error', response?.code || 0);
  }

  ApiErrors.checkOnApiError(response);
};

const showLikeRequestError = (error: any) => {
  handleRequestError(error, { showFallback: true });
};

export const likeImage = createAsyncThunk<void, ILikeImageRequest, { state: RootState }>(
  'selection/likeImage',
  async ({ imageId, optimisticUpdate = true }, { getState, dispatch }) => {
    const { selection, collection, images, endCustomer } = getState();

    if (optimisticUpdate && selection.editDraft) {
      const draftImage = images.images.find((image) => image._id === imageId);

      if (!draftImage || draftImage.hiddenBy?.includes(endCustomer._id)) return;

      const sourceSelection = selection.all.find(
        (item) => item._id === selection.editDraft?.sourceSelectionId
      );
      const addingImage = !selection.editDraft.imageIds.includes(imageId);
      const { maxLikes } = sourceSelection || {};

      if (addingImage && maxLikes && selection.editDraft.imageIds.length >= maxLikes) {
        toast.error(getTranslationKey('selections.maxImagesSelected', { limit: maxLikes }), {
          theme: 'colored',
          autoClose: 2000,
          hideProgressBar: true
        });

        return;
      }

      dispatch(toggleSelectionEditImage(imageId));

      return;
    }

    if (optimisticUpdate) {
      const numberOfLikeIncludingCurrentImage = selection.likes.length + 1;
      const { maxLikes } = selection.currentSelection;

      if (
        maxLikes &&
        maxLikes > 0 &&
        maxLikes < numberOfLikeIncludingCurrentImage &&
        !selection.likes.includes(imageId)
      ) {
        toast.error(getTranslationKey('selections.maxImagesSelected', { limit: maxLikes }), {
          theme: 'colored',
          autoClose: 2000,
          hideProgressBar: true
        });

        return;
      }

      dispatch(handleLikes(imageId));
    }

    // Serialize requests so the first like can create the active selection before later likes run.
    if (isLikeRequestProcessing) {
      likeRequestQueue.push(imageId);

      return;
    }
    isLikeRequestProcessing = true;

    try {
      const token = await fetchCustomerToken(collection._user);

      if (!token) {
        throw getUnavailableLikeRequestError();
      }

      const customerSelectionId = await getCustomerSelectionId(
        selection,
        dispatch,
        collection._user
      );

      if (!customerSelectionId) {
        throw getUnavailableLikeRequestError();
      }

      const bodyData = {
        collection_id: window.SITE_ID,
        selection_id: customerSelectionId,
        image_id: imageId
      };

      const response = await Api.TempCustomer.like(bodyData);

      checkLikeRequestResponse(response);
    } catch (e: any) {
      dispatch(handleLikes(imageId));
      checkIfAuthorizedAndLogoutIfNot(e.code);
      showLikeRequestError(e);
      console.log('e', e);
    } finally {
      isLikeRequestProcessing = false;

      const nextImageId = likeRequestQueue.shift();

      if (nextImageId) {
        dispatch(likeImage({ imageId: nextImageId, optimisticUpdate: false }));
      }
    }
  }
);

export const cancelSelectionAndDeleteIfUnsaved = createAsyncThunk<void, void, { state: RootState }>(
  'selection/cancelSelection',
  async (_, { getState, dispatch }) => {
    try {
      const { selection, collection } = getState();
      const currentSelection = selection.all.find((item: ISelection) => item.current);

      const selectionId = currentSelection?._id;

      if (!selectionId) throw new Error('No selection active');

      const unsetData = {
        collection_id: window.SITE_ID,
        selection_id: selectionId
      };

      await fetchCustomerToken(collection._user);
      const response = await Api.TempCustomer.unsetSelection(unsetData);
      ApiErrors.checkOnApiError(response);

      // only delete the selection if it doesn't have a name yet and thus isn't saved
      if (!currentSelection.name) {
        await Api.TempCustomer.deleteSelection(selectionId);
      }

      dispatch(getEndCustomer());
    } catch (e: any) {
      checkIfAuthorizedAndLogoutIfNot(e.code);

      console.log('e', e);
    }
  }
);

export const persistSelection = async ({
  values,
  state,
  dispatch,
  predefinedSelectionId,
  updateSelectionState
}: ISelectionSaveRequest) => {
  const { selection, collection } = state;
  const selectionId = selection.all.find((item: ISelection) => item.current)?._id;
  const activeSelectionId = predefinedSelectionId || selectionId;
  const selectionData = {
    collection_id: window.SITE_ID,
    selection_id: activeSelectionId,
    name: values.selectionName,
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    message: values.message
  };

  await fetchCustomerToken(collection._user);

  const currentSelection = updateSelectionState
    ? await Api.Selection.updateSelection(selectionData)
    : await Api.Selection.saveSelection(selectionData);

  ApiErrors.checkOnApiError(currentSelection);

  const unsetResponse = await Api.TempCustomer.unsetSelection({
    collection_id: window.SITE_ID,
    selection_id: activeSelectionId
  });

  ApiErrors.checkOnApiError(unsetResponse);
  dispatch(getEndCustomer());

  return currentSelection;
};

// New-design bulk actions: one `like-bulk` request toggles every passed image. Local selection
// state is committed only after the backend confirms the operation.
const bulkLike =
  (imageIds: string[], like: boolean): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    if (!imageIds.length) return;

    const initialSelection = getState().selection;
    const currentSelection = initialSelection.editDraft
      ? initialSelection.all.find(
          (item) => item._id === initialSelection.editDraft?.sourceSelectionId
        )
      : initialSelection.all.find((item) => item.current);
    let scopedImageIds = imageIds;

    if (like && currentSelection?.maxLikes && currentSelection.maxLikes > 0) {
      const newImageIds = imageIds.filter((id) => !initialSelection.likes.includes(id));
      const remaining = Math.max(currentSelection.maxLikes - initialSelection.likes.length, 0);

      if (newImageIds.length > remaining) {
        toast.error(
          getTranslationKey('selections.maxImagesSelected', { limit: currentSelection.maxLikes }),
          { theme: 'colored', autoClose: 2000, hideProgressBar: true }
        );
      }

      const allowedNewIds = new Set(newImageIds.slice(0, remaining));
      scopedImageIds = imageIds.filter(
        (id) => initialSelection.likes.includes(id) || allowedNewIds.has(id)
      );
    }

    if (!scopedImageIds.length) return;

    if (initialSelection.editDraft) {
      dispatch(
        like ? addSelectionEditImages(scopedImageIds) : removeSelectionEditImages(scopedImageIds)
      );

      return;
    }

    try {
      const { selection, collection } = getState();
      const token = await fetchCustomerToken(collection._user);

      if (!token) {
        throw getUnavailableLikeRequestError();
      }

      const selectionId = await getCustomerSelectionId(selection, dispatch, collection._user);

      if (!selectionId) {
        throw getUnavailableLikeRequestError();
      }

      const response = await Api.TempCustomer.likeBulk({
        collection_id: window.SITE_ID,
        selection_id: selectionId,
        image_ids: scopedImageIds,
        like
      });

      checkLikeRequestResponse(response);
      dispatch(like ? addLikes(scopedImageIds) : removeLikes(scopedImageIds));
    } catch (e: any) {
      checkIfAuthorizedAndLogoutIfNot(e.code);
      showLikeRequestError(e);
      console.log('e', e);
    }
  };

export const selectAllImages =
  (visibleImageIds?: string[]): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const { images, selection } = getState();
    const visibleIds = visibleImageIds ? new Set(visibleImageIds) : null;

    const imageIds = images.images
      .filter(
        (image) =>
          image.gallery_id &&
          (!visibleIds || visibleIds.has(image._id)) &&
          !selection.likes.includes(image._id)
      )
      .map((image) => image._id);

    return dispatch(bulkLike(imageIds, true));
  };

export const deselectAllImages =
  (visibleImageIds?: string[]): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const visibleIds = visibleImageIds ? new Set(visibleImageIds) : null;
    const imageIds = getState().selection.likes.filter((id) => !visibleIds || visibleIds.has(id));

    return dispatch(bulkLike(imageIds, false));
  };

export const startFreshSelection =
  (): ThunkAction<Promise<ISelection | null>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const { collection } = getState();

    if (collection.collectionCreator?.featureFlags?.newDesign !== true) return null;

    try {
      await fetchCustomerToken(collection._user);
      const newSelection = await Api.TempCustomer.createSelection();
      ApiErrors.checkOnApiError(newSelection);

      await Api.TempCustomer.setSelection({
        collection_id: collection._id,
        selection_id: newSelection._id
      });

      // The detail-page action always starts from an empty selection, even if another selection
      // happened to be active in the current browser session.
      dispatch(finishSelection());
      dispatch(addSelection({ ...newSelection, current: true }));
      dispatch(startSelection());
      await (dispatch as any)(getEndCustomer());
      // Empty current selections are normally considered "not started" by the legacy reducer.
      // The explicit detail-page Select action nevertheless enters selection mode immediately.
      dispatch(startSelection());

      return newSelection;
    } catch (e: any) {
      checkIfAuthorizedAndLogoutIfNot(e.code, collection._user);
      console.log('e', e);

      return null;
    }
  };

export const commitSelectionEdits = createAsyncThunk<
  ISelection,
  { mode: 'overwrite' | 'new' },
  { state: RootState; rejectValue: string }
>(
  'scrAppBookNew/selectionWorkflow/commitSelectionEdits',
  async ({ mode }, { dispatch, getState, rejectWithValue }) => {
    const { collection, endCustomer, selection } = getState();
    const { editDraft } = selection;
    const sourceSelection = selection.all.find((item) => item._id === editDraft?.sourceSelectionId);

    if (
      collection.collectionCreator?.featureFlags?.newDesign !== true ||
      !editDraft ||
      !sourceSelection ||
      sourceSelection.completed
    ) {
      return rejectWithValue('invalid-selection-edit');
    }

    let createdSelectionId: string | null = null;

    try {
      await fetchCustomerToken(collection._user);
      let savedSelection: ISelection;

      const toggleSelectionImages = async (selectionId: string, imageIds: string[]) => {
        await Promise.all(
          imageIds.map(async (imageId) => {
            const response = await Api.TempCustomer.like({
              collection_id: collection._id,
              selection_id: selectionId,
              image_id: imageId
            });

            ApiErrors.checkOnApiError(response);
          })
        );
      };

      if (mode === 'overwrite') {
        const originalImageIds = new Set(editDraft.originalImageIds);
        const editedImageIds = new Set(editDraft.imageIds);
        const addedImageIds = editDraft.imageIds.filter(
          (imageId) => !originalImageIds.has(imageId)
        );
        const removedImageIds = editDraft.originalImageIds.filter(
          (imageId) => !editedImageIds.has(imageId)
        );

        await toggleSelectionImages(editDraft.sourceSelectionId, [
          ...addedImageIds,
          ...removedImageIds
        ]);

        savedSelection = await Api.Selection.updateSelection({
          collection_id: collection._id,
          selection_id: editDraft.sourceSelectionId,
          name: sourceSelection.name,
          firstName: endCustomer.firstName,
          lastName: endCustomer.lastName,
          email: endCustomer.email
        });
      } else {
        const editableSourceImageIds = new Set(editDraft.originalImageIds);
        const retainedLikedImageIds = sourceSelection.favorites
          .filter((favorite) => favorite.like && !editableSourceImageIds.has(favorite._image))
          .map((favorite) => favorite._image);
        const likedImageIds = Array.from(
          new Set([...editDraft.imageIds, ...retainedLikedImageIds])
        );

        const newSelection = await Api.TempCustomer.createSelectionFromImages({
          collection_id: window.SITE_ID,
          image_ids: likedImageIds
        });
        ApiErrors.checkOnApiError(newSelection);
        const newSelectionId = newSelection._id as string;
        createdSelectionId = newSelectionId;

        await Promise.all(
          sourceSelection.favorites
            .filter((favorite) => !!favorite.comment && likedImageIds.includes(favorite._image))
            .map(async (favorite) => {
              const commentResponse = await Api.TempCustomer.comment({
                collection_id: collection._id,
                selection_id: newSelectionId,
                image_id: favorite._image,
                comment: favorite.comment
              });

              ApiErrors.checkOnApiError(commentResponse);
            })
        );

        savedSelection = await Api.Selection.updateSelection({
          collection_id: collection._id,
          selection_id: newSelectionId,
          name: sourceSelection.name,
          firstName: endCustomer.firstName,
          lastName: endCustomer.lastName,
          email: endCustomer.email
        });
        ApiErrors.checkOnApiError(savedSelection);
        createdSelectionId = null;
      }

      ApiErrors.checkOnApiError(savedSelection);
      await (dispatch as any)(getEndCustomer());

      const refreshedSelection = getState().selection.all.find(
        (item) => item._id === savedSelection._id
      );

      // The update endpoint does not always include populated favorites. getEndCustomer above
      // provides the canonical selection that the detail page needs immediately after saving.
      return (refreshedSelection || savedSelection) as ISelection;
    } catch (e: any) {
      if (createdSelectionId) {
        try {
          await Api.TempCustomer.deleteSelection(createdSelectionId);
        } catch (cleanupError) {
          console.log('selection cleanup failed', cleanupError);
        }
      }

      checkIfAuthorizedAndLogoutIfNot(e.code, collection._user);

      return rejectWithValue(e?.message || 'selection-edit-failed');
    }
  }
);

export const setActiveSelection =
  (
    selection_id: string,
    collection: ICollection
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      await fetchCustomerToken(collection._user);
      await Api.TempCustomer.setSelection({ selection_id, collection_id: collection._id });

      dispatch(getEndCustomer());
    } catch (error) {
      console.log(error);
    }
  };
